import React, { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { observer } from "mobx-react-lite";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import { classNames } from "../../helpers";

interface MessageType {
  name: string;
  description: string;
  key: string;
  disabled: boolean;
  icon?: string;
}

interface VoterReachOutHeaderProps {
  popOver: boolean;
  setPopOver: (value: boolean) => void;
  addNewReachOut: (messageType: string) => void;
  addNewButtonRef: React.RefObject<HTMLElement>;
}

const datasetTypes: MessageType[] = [
  {
    name: "Text Message",
    description: "Type text messages",
    key: "Text Messages",
    disabled: true,
    icon: "💬",
  },
  {
    name: "Voice Message",
    description: "Record a voice message",
    key: "Voice Messages",
    disabled: false,
    icon: "🎤",
  },
  {
    name: "WhatsApp Message",
    description: "Send a WhatsApp message",
    key: "whatsapp Messages",
    disabled: false,
    icon: "💚",
  },
  {
    name: "Social Media Post",
    description: "Create a social media post",
    key: "Social Media Post",
    disabled: true,
    icon: "📱",
  },
];

const VoterReachOutHeader = observer((props: VoterReachOutHeaderProps) => {
  const { popOver, setPopOver, addNewReachOut, addNewButtonRef } = props;

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            ref={addNewButtonRef as React.RefObject<HTMLButtonElement>}
            onClick={() => setPopOver(!popOver)}
            className="inline-flex items-center rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-md shadow-indigo-200/60 transition-all hover:-translate-y-0.5 hover:from-indigo-700 hover:to-violet-700 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          >
            <span className="mr-2 text-lg leading-none" aria-hidden="true">
              +
            </span>
            <span>Create Message</span>
            <ChevronDownIcon
              className={classNames(
                open ? "text-gray-200" : "text-gray-300",
                "ml-2 h-5 w-5 transition-transform",
                open && "rotate-180 transform"
              )}
              aria-hidden="true"
            />
          </Popover.Button>

          <Transition
            show={popOver}
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel static className="absolute right-0 z-10 mt-2 w-64 origin-top-right sm:w-72">
              <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="relative grid gap-1 bg-white p-2">
                  {datasetTypes.map((item) => (
                    <button
                      key={item.name}
                      type="button"
                      title={item.disabled ? "Coming soon" : ""}
                      disabled={item.disabled}
                      onClick={() => {
                        if (item.disabled) return;
                        addNewReachOut(item.key);
                        setPopOver(false);
                      }}
                      className={classNames(
                        "rounded-md p-3 text-left transition-colors",
                        item.disabled
                          ? "cursor-not-allowed bg-gray-50 text-gray-400 opacity-60"
                          : "cursor-pointer text-gray-900 hover:bg-indigo-50 active:bg-indigo-100"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {item.icon && <span className="text-lg">{item.icon}</span>}
                        <div>
                          <p
                            className={classNames(
                              "text-sm font-semibold",
                              item.disabled ? "text-gray-400" : "text-gray-900"
                            )}
                          >
                            {item.name}
                          </p>
                          <p
                            className={classNames(
                              "mt-1 text-xs",
                              item.disabled ? "italic text-gray-400" : "text-gray-500"
                            )}
                          >
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
});

export default VoterReachOutHeader;
